import { useGetTest } from "@/queries/tests/queries";
import { PublicQuestion } from "@/queries/types";
import styled from "@emotion/styled";
import { DSNumericAnswer as SystemNumericAnswer } from "@hundred5/design-system";
import { t as tCoreMacro } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useRouter } from "next/router";
import { Dispatch } from "react";
import { isIOS } from "react-device-detect";
import * as amplitude from "../../../../amplitude";
import { Action, State } from "../../reducer";
import DebouncedAnswer from "./debounced-answer";

interface Props {
  question: PublicQuestion;
  dispatch: Dispatch<Action>;
  state: State;
}

export function NumericAnswer({ question, dispatch, state }: Props) {
  const { t } = useLingui();
  const { query } = useRouter();
  const jobOpeningSlug = query.slug as string;
  const hashedTestId = query.hashedTestId as string;
  const testQuery = useGetTest(jobOpeningSlug, hashedTestId!, !!hashedTestId);
  const answer = testQuery.data?.active_live_test?.answers?.find(
    (answer) => answer.question_id === question.test_question_id,
  );

  return (
    <DebouncedAnswer
      answer={answer?.free_answer_content || ""}
      onChange={(content) => {
        const error = validate(content);

        if (error == null) {
          dispatch({
            type: "enter_text_answer",
            payload: {
              questionId: question.test_question_id.toString(),
              freeTextAnswer: content,
            },
          });

          amplitude.trackQuestionAnswer(
            state.jobOpeningSlug,
            state.activeQuestionIndex,
          );
        }
      }}
    >
      {({ answer, onChange, onBlur }) => {
        const error = validate(answer);

        return (
          <Container>
            {error != null ? <ErrorMessage>{error}</ErrorMessage> : null}

            <Field
              value={answer}
              placeholder={t`Write answer here...`}
              inputMode={isIOS ? "text" : "decimal"}
              disabled={state.submitting}
              onChange={(event) => {
                onChange(event.target.value);
              }}
              onBlur={onBlur}
            />
          </Container>
        );
      }}
    </DebouncedAnswer>
  );
}

//region Utils
const validate = (value: string): string | null => {
  if (value.length > 0 && !numberRegEx.test(value)) {
    return tCoreMacro`Please enter a numeric value such as 491 or -32.19.`;
  }

  return null;
};

const numberRegEx = /^-?(0|[1-9]\d*)(\.\d+)?$/;
//endregion

//region Styles
const Container = styled.div``;

const Field = styled(SystemNumericAnswer)`
  margin-bottom: 20px;
`;

const ErrorMessage = styled.div`
  color: ${(props) => props.theme.colors.red[100]};
  background: ${(props) => props.theme.colors.red[10]};
  border-radius: 8px;
  padding: 4px 12px;
  margin-bottom: 12px;
`;
//endregion
